/**
 * Sewing Hourly Board — one factory, one work day, output by the hour against the plan,
 * on a wall. See `design-notes.md`.
 *
 * Four queries, one board. `lines` is the fixed left and right columns, `timeslots` the hourly
 * cells, `hours` the column set and the floor total under each, `totals` the headline strip and the
 * totals row. They are four governed views rather than one query the browser reshapes because the
 * legacy screen computed all of it in JavaScript, and §5.2 puts that arithmetic in ADB.
 *
 * The one client-side reshape is keying the cells onto their lines — a lookup, not a calculation
 * (`grid.ts`), gated on a complete result for both `timeslots` and `hours` (§4.3).
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DataTable } from '@/components/DataTable';
import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ButtonGroupSelector } from '@/components/report/ButtonGroupSelector';
import { ExportMenu } from '@/components/report/ExportMenu';
import { KioskToggle } from '@/components/report/KioskToggle';
import { exportColumns } from '@/components/report/groupedColumns';
import { useReportFilters, useReportSort } from '@/components/report/useReportFilters';
import { useKioskMode } from '@/hooks/useKioskMode';
import { useReportQuery } from '@/hooks/useReportQuery';
import type { ReportQueryRequest, SortSpec } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { resolveFactory } from '@/lib/factories';

import { BoardHeadline, type BoardTotals } from './cards';
import {
  BoardLegend,
  DayFooter,
  HourFooter,
  count,
  footerLabelClass,
  hourColumnId,
  sewingHourlyBoardColumns,
} from './columns';
import { useFittedTier } from './fit';
import { useAutoPage } from './useAutoPage';
import {
  METRIC_PARAM,
  METRIC_TABS,
  REFRESH_INTERVAL_MS,
  defaultDate,
  sewingHourlyBoardFilters,
  parseMetric,
} from './filters';
import { hourColumns, indexCells, isComplete, type CellRow, type HourRow, type LineRow } from './grid';

const REPORT_ID = 'sewing-hourly-board';

/** The label on the chip, not the line code — matches the SQL fallback in lines.sql. */
const DEFAULT_SORT: SortSpec[] = [{ field: 'line_name', dir: 'asc' }];

/**
 * Legacy refreshed every 15 minutes once a search had run. Background true so a wall screen or an
 * unfocused tab keeps updating — TanStack pauses intervals on a hidden document otherwise.
 */
const WALL_POLL = {
  staleTime: REFRESH_INTERVAL_MS,
  refetchInterval: REFRESH_INTERVAL_MS,
  refetchIntervalInBackground: true,
} as const;

export default function SewingHourlyBoardReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, status } = useAuth();
  const { isKiosk } = useKioskMode();

  const factory = resolveFactory(searchParams.get('factory'), user?.defaultFactory);
  const metric = parseMetric(searchParams.get(METRIC_PARAM));

  const schema = useMemo(() => sewingHourlyBoardFilters(factory), [factory]);
  const { values, setValue } = useReportFilters(schema);
  const { sort, setSort } = useReportSort(DEFAULT_SORT);

  // replace, not push — Back should leave the report rather than walk back through the toggle.
  const setUrlParam = useCallback(
    (name: string, value: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(name, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Write the factory into the URL, or a shared link resolves to the *recipient's* factory — a
  // different answer under the same address.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (searchParams.get('factory') === factory) return;
    setValue('factory', factory);
  }, [status, searchParams, factory, setValue]);

  const tranDate = typeof values['tran_date'] === 'string' && values['tran_date'] !== ''
    ? values['tran_date']
    : defaultDate();

  // Multi-selects: empty string / missing = all (server `_any = 1`). Only pass a list when chosen.
  const building =
    typeof values['building'] === 'string' && values['building'] !== ''
      ? values['building']
      : undefined;
  const floor =
    typeof values['floor'] === 'string' && values['floor'] !== '' ? values['floor'] : undefined;

  const requestFilters = useMemo<ReportQueryRequest['filters']>(
    () => ({
      tran_date: tranDate,
      factory,
      metric,
      ...(building !== undefined ? { building } : {}),
      ...(floor !== undefined ? { floor } : {}),
    }),
    [tranDate, factory, metric, building, floor],
  );

  const lines = useReportQuery<LineRow>(REPORT_ID, requestFilters, {
    view: 'lines',
    ...WALL_POLL,
  });
  const cells = useReportQuery<CellRow>(REPORT_ID, requestFilters, {
    view: 'timeslots',
    ...WALL_POLL,
  });
  const hoursQuery = useReportQuery<HourRow>(REPORT_ID, requestFilters, {
    view: 'hours',
    ...WALL_POLL,
  });
  const totals = useReportQuery<BoardTotals>(REPORT_ID, requestFilters, {
    view: 'totals',
    ...WALL_POLL,
  });

  // The §4.3 gate. The board is a whole-result reshape: a short `timeslots` result would drop cells
  // silently, and a short `hours` result would drop entire columns out of the header.
  const gridComplete = isComplete(cells.data) && isComplete(hoursQuery.data);

  const hours = useMemo(
    () => (gridComplete ? hourColumns(hoursQuery.data?.rows ?? []) : []),
    [gridComplete, hoursQuery.data],
  );
  const cellIndex = useMemo(
    () => indexCells(gridComplete ? (cells.data?.rows ?? []) : []),
    [gridComplete, cells.data],
  );

  const rows = useMemo(() => lines.data?.rows ?? [], [lines.data]);
  // In kiosk the board must fit with no scroll and no pager, so the density is measured, not guessed.
  const { ref: boardRef, tier, pageSize } = useFittedTier(isKiosk, rows.length);

  // A floor too tall for its screen rotates through its lines instead of hiding the ones below the
  // fold — nobody walks up to a wall TV to scroll. `pageSize` is null whenever everything fits.
  //
  // The measured size says how many rows *can* fit; the rows are then spread evenly over that many
  // pages rather than filling each to the brim. Thirty lines on a screen holding twenty is 15 and
  // 15, not 20 and a stub of 10 — a half-empty last page reads as a floor that lost some lines,
  // and the pages visibly changing shape is the thing the eye catches on a wall.
  const pageCount = pageSize === null ? 1 : Math.ceil(rows.length / pageSize);
  const balancedSize = pageSize === null ? null : Math.ceil(rows.length / pageCount);
  const pageIndex = useAutoPage(pageCount);

  const totalsRow = totals.data?.rows[0];

  /**
   * Where the tick sits on every line's day bar: the share of the day's hours already finished.
   * Both figures come from `totals`; this is the mark's position on a track, not a measure.
   */
  const paceMarkPct = useMemo(() => {
    const elapsed = totalsRow?.['elapsed_timeslot_count'];
    const total = totalsRow?.['timeslot_count'];
    if (typeof elapsed !== 'number' || typeof total !== 'number' || total <= 0) return null;
    return (elapsed / total) * 100;
  }, [totalsRow]);

  const columns = useMemo(
    () => sewingHourlyBoardColumns({ hours, cells: cellIndex, tier, paceMarkPct }),
    [hours, cellIndex, tier, paceMarkPct],
  );

  /**
   * The floor total row. Every figure comes from `totals` or `hours` — none is summed here, because
   * each is a ratio or a whole-selection total that §4.3 keeps in SQL.
   */
  const footerTotals = useMemo(() => {
    if (totalsRow === undefined || !gridComplete || lines.data?.truncated === true) {
      return undefined;
    }
    const values: Record<string, React.ReactNode> = {
      planned_quantity_per_hour: (
        <span className="block text-right tabular-nums">
          {count(Number(totalsRow['planned_quantity_per_hour'] ?? 0))}
        </span>
      ),
      total_output: (
        <DayFooter
          tier={tier}
          output={Number(totalsRow['total_output'] ?? 0)}
          planDay={Number(totalsRow['planned_quantity_per_day'] ?? 0)}
          status={totalsRow['pace_status']}
        />
      ),
    };
    for (const hour of hours) {
      values[hourColumnId(hour)] = <HourFooter hour={hour} tier={tier} />;
    }
    return {
      labelColumn: 'line_name',
      label: <span className={footerLabelClass(tier)}>Floor total</span>,
      values,
      format: (value: number) => count(value),
    };
  }, [totalsRow, gridComplete, lines.data?.truncated, hours, tier]);

  const isLoading = lines.isLoading || cells.isLoading || hoursQuery.isLoading;
  const error = lines.error ?? cells.error ?? hoursQuery.error ?? totals.error;

  const request: ReportQueryRequest = { view: 'lines', filters: requestFilters, sort };

  return (
    <ReportPage
      title="Sewing Hourly Board"
      description="Output by the hour against the plan, for one factory and one work day."
      kiosk={isKiosk}
      // On a wall the floor is what needs naming; which report it is, the reader knows.
      kioskHeading={`Sewing hourly — ${factory}${building === undefined ? '' : ` · ${building}`} · ${metric}`}
      isLoading={isLoading}
      error={error}
      generatedAt={lines.data?.generatedAt}
      dataAsOf={lines.data?.dataAsOf}
      truncated={lines.data?.truncated === true}
      summary={
        <BoardHeadline totals={totalsRow} isLoading={totals.isLoading} tier={tier} />
      }
      filters={
        isKiosk ? undefined : (
          <FilterBar
            schema={schema}
            afterFilters={
              <ButtonGroupSelector
                label="Output"
                value={metric}
                options={METRIC_TABS}
                onChange={(next) => {
                  setUrlParam(METRIC_PARAM, next);
                }}
              />
            }
            actions={
              <>
                {/* The `lines` view: the hourly leaves carry no `exportLabel`, so `exportColumns`
                    drops them and the file holds the declared columns. See improvements.md — the
                    legacy button exported the wide grid. */}
                <ExportMenu
                  reportId={REPORT_ID}
                  request={request}
                  columns={exportColumns(columns)}
                />
                <KioskToggle />
              </>
            }
          />
        )
      }
      table={
        <div className="flex min-h-0 flex-1 flex-col">
          {/* The measured box: the height left once the strip, the kiosk bar and the legend have taken theirs. */}
          <div ref={boardRef} className="flex min-h-0 flex-1 flex-col">
            <DataTable
              columns={columns}
              data={rows}
              isLoading={isLoading}
              sort={sort}
              onSortChange={setSort}
              // No totals row on a wall: the headline strip already carries the floor's figures,
              // and the height it gives back goes to the lines, which are what the board is for.
              totals={isKiosk ? undefined : footerTotals}
              truncated={lines.data?.truncated === true}
              // Board dressing in both modes: no zebra banding, which would fight the cell colours.
              variant="board"
              // In kiosk, fill the height and drop the pager — a line on page two is a line nobody is watching.
              fitToScreen={isKiosk}
              page={balancedSize === null ? undefined : { index: pageIndex, size: balancedSize }}
              className={isKiosk ? 'min-h-0 flex-1 border-0 bg-transparent' : undefined}
              caption="Hourly output by production line, against the plan for each hour"
              emptyMessage="Nothing was recorded for these filters. Try another work date, or widen the building and floor."
            />
          </div>
          <BoardLegend
            tier={tier}
            page={
              balancedSize === null
                ? undefined
                : {
                    index: pageIndex,
                    count: pageCount,
                    from: pageIndex * balancedSize + 1,
                    to: Math.min((pageIndex + 1) * balancedSize, rows.length),
                    total: rows.length,
                  }
            }
          />
        </div>
      }
    />
  );
}
